import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import UploadIcon from '@mui/icons-material/Upload';
import { useSnackbar } from 'notistack';
import { documentTypes } from '../checkInData';
import { uploadTenantDocument, validateCheckInBooking } from '../checkInService';
import ConfirmCheckInDialog from '../components/ConfirmCheckInDialog';
import PrimaryActionButton from '../components/PrimaryActionButton';

function isImageFile(file) {
  return file?.type?.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(file?.name || '');
}

function getDocumentImageUrl(tenantUuid, documentType) {
  if(!tenantUuid){
    return '';
  }

  return `/rest/user/${tenantUuid}/document/${documentType}`;
}

function hasBookingDetails(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && value.id);
}

function getInitialCheckInDetails() {
  return {
    checkInDate: '',
    checkInType: '',
    rentCollectionDay: '',
    numberOfWeeksDays: '',
  };
}

function getCheckInDateFromBooking(bookingDetails = {}) {
  const dateParts = String(bookingDetails.joiningDate || '').split('-');

  if(dateParts.length !== 3){
    return '';
  }

  const [day, month, year] = dateParts;

  if(!day || !month || !year){
    return '';
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

function DocumentsStep({ pgId, tenantUuid, rentCollectionType, pgDefaults, onProceed, onSubmitSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState({});
  const [failedDocumentUrls, setFailedDocumentUrls] = useState({});
  const [documentUploads, setDocumentUploads] = useState({});
  const [isValidatingBooking, setIsValidatingBooking] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [checkInDetails, setCheckInDetails] = useState(getInitialCheckInDetails);
  const isUploadingDocument = Object.values(documentUploads).some(status => status?.isUploading);

  useEffect(() => {
    setFailedDocumentUrls({});
  }, [tenantUuid]);

  const handleDocumentChange = (key, documentType) => async (event) => {
    const input = event.target;
    const file = input.files?.[0];

    if(!file){
      return;
    }

    if(!isImageFile(file)){
      enqueueSnackbar('Please upload an image file', {variant: 'error'});
      input.value = '';
      return;
    }

    if(!tenantUuid){
      enqueueSnackbar('Submit profile details before uploading documents', {variant: 'error'});
      input.value = '';
      return;
    }

    setDocuments(prevDocuments => ({
      ...prevDocuments,
      [key]: file,
    }));
    setDocumentUploads(prevUploads => ({
      ...prevUploads,
      [key]: { isUploading: true, error: '' },
    }));

    try{
      const {response, error} = await uploadTenantDocument({
        uuid: tenantUuid,
        file,
        fileName: file.name,
        documentType,
      });

      if(error){
        setDocumentUploads(prevUploads => ({
          ...prevUploads,
          [key]: { isUploading: false, error: error?.message || 'Upload failed' },
        }));
        enqueueSnackbar(error?.message || 'Unable to upload document', {variant: 'error'});
        return;
      }

      setDocumentUploads(prevUploads => ({
        ...prevUploads,
        [key]: { isUploading: false, error: '', isUploaded: true },
      }));
      setFailedDocumentUrls(prevFailedDocumentUrls => ({
        ...prevFailedDocumentUrls,
        [key]: false,
      }));
      enqueueSnackbar(response?.message || 'Document uploaded successfully', {variant: 'success'});
    }catch(error){
      setDocumentUploads(prevUploads => ({
        ...prevUploads,
        [key]: { isUploading: false, error: 'Upload failed' },
      }));
      enqueueSnackbar(error?.message || 'Unable to upload document', {variant: 'error'});
    }finally{
      input.value = '';
    }
  };

  const handleProceed = async () => {
    if(!pgId){
      enqueueSnackbar('Property details are missing. Please reload and try again.', {variant: 'error'});
      return;
    }

    if(!tenantUuid){
      enqueueSnackbar('Tenant details are missing. Please complete profile details first.', {variant: 'error'});
      return;
    }

    setIsValidatingBooking(true);

    try{
      const {response, error} = await validateCheckInBooking(pgId, tenantUuid);

      if(error){
        onProceed?.();
        return;
      }

      const apiBookingDetails = response?.data || response;
      if(hasBookingDetails(apiBookingDetails)){
        setBookingDetails(apiBookingDetails);
        setCheckInDetails({
          ...getInitialCheckInDetails(),
          checkInDate: getCheckInDateFromBooking(apiBookingDetails),
        });
        return;
      }

      onProceed?.();
    }catch(error){
      enqueueSnackbar(error?.message || 'Unable to validate booking details', {variant: 'error'});
    }finally{
      setIsValidatingBooking(false);
    }
  };

  const closeBookingDialog = () => {
    setBookingDetails(null);
    setCheckInDetails(getInitialCheckInDetails());
  };

  const handleBookingSubmitSuccess = () => {
    setBookingDetails(null);
    setCheckInDetails(getInitialCheckInDetails());
    onSubmitSuccess?.();
  };

  return (
    <Box>
      <Typography variant="h5" textAlign="center" sx={{ mb: 4, lineHeight: 1.5 }}>
        We need few documents for the Check-In, please upload them to proceed.
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, md: 2.5 }}
        alignItems={{ xs: 'center', md: 'stretch' }}
      >
        {documentTypes.map((documentType) => (
          <DocumentUploadCard
            key={documentType.key}
            label={documentType.label}
            file={documents[documentType.key]}
            fileUrl={failedDocumentUrls[documentType.key] ? '' : getDocumentImageUrl(tenantUuid, documentType.documentType)}
            uploadStatus={documentUploads[documentType.key]}
            onChange={handleDocumentChange(documentType.key, documentType.documentType)}
            onImageError={() => setFailedDocumentUrls(prevFailedDocumentUrls => ({
              ...prevFailedDocumentUrls,
              [documentType.key]: true,
            }))}
          />
        ))}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <PrimaryActionButton disabled={isUploadingDocument || isValidatingBooking} onClick={handleProceed}>
          {isValidatingBooking ? 'Validating...' : 'Proceed'}
        </PrimaryActionButton>
      </Box>
      <ConfirmCheckInDialog
        open={Boolean(bookingDetails)}
        pgId={pgId}
        tenantUuid={tenantUuid}
        bookingDetails={bookingDetails}
        checkInDetails={checkInDetails}
        sharingType={bookingDetails?.sharingType}
        rentCollectionType={rentCollectionType}
        pgDefaults={pgDefaults}
        title="Confirm Check-In Details"
        onClose={closeBookingDialog}
        onDetailsChange={setCheckInDetails}
        onSubmitSuccess={handleBookingSubmitSuccess}
      />
    </Box>
  );
}

function DocumentUploadCard({ label, file, fileUrl = '', uploadStatus = {}, onChange, onImageError }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const isUploading = uploadStatus.isUploading;

  useEffect(() => {
    if(!file){
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const imageUrl = previewUrl || fileUrl;

  return (
    <Paper
      component="label"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        maxWidth: { xs: 360, sm: 420, md: 'none' },
        minWidth: 0,
        minHeight: { xs: 220, md: 250 },
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
      }}
    >
      <input type="file" accept="image/*" hidden disabled={isUploading} onChange={onChange} />
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          flex: 1,
          minHeight: { xs: 180, md: 200 },
          p: 2,
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={`${label} preview`}
            onError={onImageError}
            sx={{
              width: '100%',
              height: { xs: 150, md: 160 },
              objectFit: 'contain',
              borderRadius: 3,
              bgcolor: 'grey.100',
            }}
          />
        ) : (
          <CameraAltIcon sx={{ fontSize: 110, color: 'grey.400' }} />
        )}
        {file && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, px: 2, textAlign: 'center' }}>
            {file.name}
          </Typography>
        )}
        {isUploading && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Uploading...
            </Typography>
          </Stack>
        )}
        {!isUploading && uploadStatus.isUploaded && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            Uploaded
          </Typography>
        )}
        {!isUploading && uploadStatus.error && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1, px: 2, textAlign: 'center' }}>
            {uploadStatus.error}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          flexShrink: 0,
          bgcolor: 'grey.100',
          px: 3,
          py: 1.5,
        }}
      >
        <Typography fontWeight={700}>{label}</Typography>
        <UploadIcon color="primary" />
      </Stack>
    </Paper>
  );
}

export default DocumentsStep;
